import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {DataTableErrorsByRecordId} from 'notebook/data-tables/DataTableConstants';
import {Row, RowData} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {TriangleLocation} from 'notebook/tables/errors/ErrorTriangle';
import IllegalErrorTriangle from 'notebook/tables/errors/IllegalErrorTriangle';
import {
  getSchemaFieldApiIdForColumn,
  getSchemaFieldApiIdFromColumnConstraint,
} from 'notebook/tables/typed-values/schema/SchemaFieldIdGetters';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {Optional} from 'util/Types';

import {getAggregateColumnValidation, getAggregateRowValidation} from './AggregateValidationHelper';
import {
  AssayResultValidation,
  AssayValidationByResultId,
  AssayValidationDisplayInfo,
  FieldHasValueDictByResultId,
  ReadableAssayValidationStatus,
} from './ValidationConstants';
import ValidationIndicator from './ValidationIndicator';

function _getValidationInfoForCell(
  resultValidationForRow: AssayResultValidation,
  columnConstraintsForTable: Optional<Array<Optional<ColumnConstraint>>>,
  columnIndex: number
): AssayValidationDisplayInfo | null {
  const schemaFieldApiId = getSchemaFieldApiIdForColumn(columnConstraintsForTable, columnIndex);
  if (schemaFieldApiId) {
    return resultValidationForRow.fieldValidation[schemaFieldApiId];
  }
  return null;
}

function _getCellsWithValidationIndicators(
  row: Row,
  columnConstraintsForTable: Optional<Array<Optional<ColumnConstraint>>>,
  assayValidationById: AssayValidationByResultId,
  resultId: string | null
): Row {
  if (resultId) {
    const resultValidationForRow = assayValidationById[resultId];
    assert(
      Boolean(resultValidationForRow),
      'Expected all result IDs from `assayResultIdsByRow` to be present as keys in `assayValidationById`'
    );
    return row.map((cell, colIndex) => {
      const validationInfo = _getValidationInfoForCell(
        resultValidationForRow,
        columnConstraintsForTable,
        colIndex
      );
      const isInvalid = validationInfo && validationInfo.status === ReadableAssayValidationStatus.INVALID;
      return {
        ...cell,
        userAnnotationIndicator: (
          <ValidationIndicator assayValidationToDisplay={validationInfo} location={TriangleLocation.CELL} />
        ),
        grayOutContents: isInvalid,
        exportModifierText: isInvalid ? 'INVALID' : '',
      };
    });
  }
  return row;
}

function _getColumnConstraintWithValidationIndicator(
  columnConstraint: ColumnConstraint | null,
  assayValidationById: AssayValidationByResultId,
  fieldHasValueDictById: FieldHasValueDictByResultId
): ColumnConstraint {
  assert(
    Boolean(columnConstraint),
    'Expected table object passed to getTableWithValidationIndicators to have non-null ' +
      'column constraints'
  );
  const schemaFieldId = getSchemaFieldApiIdFromColumnConstraint(columnConstraint);
  if (schemaFieldId) {
    const validationInfo = getAggregateColumnValidation(
      assayValidationById,
      fieldHasValueDictById,
      schemaFieldId
    );
    return {
      ...columnConstraint,
      userAnnotationIndicator: (
        <ValidationIndicator
          assayValidationToDisplay={validationInfo}
          location={TriangleLocation.AGGREGATE}
        />
      ),
    };
  }
  return columnConstraint!;
}

function _getRowDataWithValidationIndicatorForAssayValidation(
  rowData: RowData,
  assayValidationById: AssayValidationByResultId,
  assaySchema: AssaySchema
): RowData {
  assert(Boolean(rowData.euid), 'Expected rowData to have `euid` property');

  if (rowData.euid! in assayValidationById) {
    const validationInfo = getAggregateRowValidation(assayValidationById[rowData.euid!], assaySchema);
    return {
      ...rowData,
      uiData: {
        userAnnotationIndicator: (
          <ValidationIndicator
            assayValidationToDisplay={validationInfo}
            location={TriangleLocation.AGGREGATE}
          />
        ),
      },
    };
  } else {
    return rowData;
  }
}

function _getRowDataWithValidationIndicatorForServerErrors(
  rowData: RowData,
  dataTableErrorsByRecordId: DataTableErrorsByRecordId,
  assaySchema: AssaySchema
): RowData {
  assert(Boolean(rowData.euid), 'Expected rowData to have `euid` property');

  const primaryLinkField = getPrimaryLinkField(assaySchema);
  const primaryLinkFieldError =
    primaryLinkField && dataTableErrorsByRecordId[rowData.euid!]?.[primaryLinkField.dbId];
  if (primaryLinkFieldError) {
    return {
      ...rowData,
      uiData: {
        userAnnotationIndicator: (
          // Same as the regular TableCell red dog ear
          <IllegalErrorTriangle
            error={{
              type: 'Error',
              message: primaryLinkFieldError,
            }}
            location={TriangleLocation.AGGREGATE}
          />
        ),
      },
    };
  } else {
    return rowData;
  }
}

export interface ValidationIndicatorGenerationData {
  assayValidationByResultId: AssayValidationByResultId | null;
  fieldHasValueDictByResultId: FieldHasValueDictByResultId | null;
  assayResultIdsByRow: Array<string | null> | null;
  assaySchema: AssaySchema;
  dataTableErrorsByRecordId: DataTableErrorsByRecordId;
}

/**
 * Returns an updated version of `fullTable` with any assay validation indicators added, based on
 * the provided ValidationIndicatorGenerationData. Components that use getTableWithValidationIndicators
 * can get the necessary `assayValidationByResultId` and `fieldHasValueDictByResultId` props via
 * getOrFetchResultValidationInConnector.
 */
export default function getTableWithValidationIndicators(
  fullTable: Table,
  validationIndicatorGenerationData: ValidationIndicatorGenerationData
): Table {
  const {
    assayValidationByResultId,
    fieldHasValueDictByResultId,
    assayResultIdsByRow,
    assaySchema,
    dataTableErrorsByRecordId,
  } = validationIndicatorGenerationData;
  assert(
    Boolean(assayValidationByResultId) === Boolean(fieldHasValueDictByResultId),
    'Expected `assayValidationByResultId` and `fieldHasValueDictByResultId` to be either both present ' +
      'or both null'
  );
  assert(
    Boolean(fullTable.rowData),
    'Expected table object passed to getTableWithValidationIndicators to have `rowData` property'
  );

  let tableWithValidationIndicators = fullTable;

  // Inject validation indicators based on assay validation
  if (
    [assayValidationByResultId, fieldHasValueDictByResultId, assayResultIdsByRow].every(
      (arg) => arg && !_.isEmpty(arg)
    )
  ) {
    tableWithValidationIndicators = {
      ...tableWithValidationIndicators,
      rowData: tableWithValidationIndicators.rowData!.map((singleRowData) =>
        _getRowDataWithValidationIndicatorForAssayValidation(
          singleRowData,
          assayValidationByResultId!,
          assaySchema
        )
      ),
      columnConstraints: tableWithValidationIndicators.columnConstraints.map((columnConstraint) =>
        _getColumnConstraintWithValidationIndicator(
          columnConstraint,
          assayValidationByResultId!,
          fieldHasValueDictByResultId!
        )
      ),
      table: tableWithValidationIndicators.table.map((row, i) =>
        _getCellsWithValidationIndicators(
          row,
          tableWithValidationIndicators.columnConstraints,
          assayValidationByResultId!,
          assayResultIdsByRow![i]
        )
      ),
    };
  }

  // Inject validation indicators based on server errors, which should take priority over any manual assay validation
  if (!_.isEmpty(dataTableErrorsByRecordId)) {
    tableWithValidationIndicators = {
      ...tableWithValidationIndicators,
      rowData: tableWithValidationIndicators.rowData!.map((singleRowData) =>
        _getRowDataWithValidationIndicatorForServerErrors(
          singleRowData,
          dataTableErrorsByRecordId,
          assaySchema
        )
      ),
    };
  }

  return tableWithValidationIndicators;
}
